import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import PeopleIcon from '@material-ui/icons/People';
import AttachMoneyIcon from '@material-ui/icons/AttachMoney';
import StarIcon from '@material-ui/icons/Star';
import { firebase } from 'common/src/config/configureFirebase';
import { ref, get } from "firebase/database";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  header: {
    marginBottom: theme.spacing(3)
  },
  tabs: {
    marginBottom: theme.spacing(3),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8
  },
  filterContainer: {
    marginBottom: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(2)
  },
  card: {
    height: '100%'
  },
  topThreeCard: {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    textAlign: 'center',
    padding: theme.spacing(3)
  },
  medalContainer: {
    fontSize: 64,
    marginBottom: theme.spacing(2)
  },
  tableContainer: {
    maxHeight: 600
  },
  avatar: {
    width: 40,
    height: 40
  },
  rankCell: {
    fontWeight: 'bold',
    fontSize: 18
  },
  topThreeRank: {
    fontSize: 24
  },
  tierChip: {
    fontWeight: 'bold'
  },
  metricValue: {
    fontWeight: 'bold',
    fontSize: 16
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 400
  }
}));

export default function AgentLeaderboards() {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [leaderboardType, setLeaderboardType] = useState(0); // 0: partners, 1: earnings, 2: performance
  const [countryFilter, setCountryFilter] = useState('all');
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [topThree, setTopThree] = useState([]);
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    fetchLeaderboard();
  }, [leaderboardType, countryFilter]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);

      // Fetch agent rewards data
      const rewardsSnapshot = await get(ref(firebase.database, 'agentRewards'));
      const rewardsData = rewardsSnapshot.val() || {};

      // Fetch user data
      const usersSnapshot = await get(ref(firebase.database, 'users'));
      const usersData = usersSnapshot.val() || {};

      // Combine and process data
      let agents = Object.entries(rewardsData)
        .map(([uid, rewards]) => {
          const user = usersData[uid] || {};
          return {
            uid,
            firstName: user.firstName || 'Unknown',
            lastName: user.lastName || '',
            email: user.email || '',
            mobile: user.mobile || '',
            country: user.country || 'Unknown',
            tier: rewards.tier || 'inactive',
            partnersCount: rewards.partnersCount || 0,
            totalEarnings: parseFloat(rewards.totalEarnings || 0),
            activationPaid: parseFloat(user.agentActivationPaid || 0),
            lastRewardCalculation: rewards.lastRewardCalculation || 0
          };
        })
        .filter(agent => agent.tier !== 'inactive');

      // Get unique countries
      const uniqueCountries = [...new Set(agents.map(a => a.country))].sort();
      setCountries(uniqueCountries);

      // Filter by country
      if (countryFilter !== 'all') {
        agents = agents.filter(agent => agent.country === countryFilter);
      }

      // Calculate performance score
      agents = agents.map(agent => {
        const tierBonus = {
          brown: 10,
          gold: 20,
          diamond: 30,
          platinum: 40
        }[agent.tier] || 0;
        
        const performanceScore = 
          (agent.partnersCount * 10) + 
          (agent.totalEarnings / 10) + 
          tierBonus;
        
        return { ...agent, performanceScore };
      });

      // Sort based on leaderboard type
      let sortField = 'partnersCount';
      if (leaderboardType === 1) sortField = 'totalEarnings';
      if (leaderboardType === 2) sortField = 'performanceScore';

      const sortedAgents = agents.sort((a, b) => b[sortField] - a[sortField]);

      // Add rank
      const rankedAgents = sortedAgents.map((agent, index) => ({
        ...agent,
        rank: index + 1
      }));

      setTopThree(rankedAgents.slice(0, 3));
      setLeaderboardData(rankedAgents);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    const colors = {
      brown: '#8B4513',
      gold: '#FFD700',
      diamond: '#00BCD4',
      platinum: '#9E9E9E'
    };
    return colors[tier] || '#ccc';
  };

  const getTierName = (tier) => {
    const names = {
      brown: 'ALPHA',
      gold: 'BETA',
      diamond: 'GAMMA',
      platinum: 'DELTA'
    };
    return names[tier] || tier;
  };

  const getRankMedal = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const getMetricValue = (agent) => {
    switch (leaderboardType) {
      case 0:
        return `${agent.partnersCount} Partners`;
      case 1:
        return `$${agent.totalEarnings.toFixed(2)}`;
      case 2:
        return `${agent.performanceScore.toFixed(0)} pts`;
      default:
        return '';
    }
  };

  const getMetricLabel = () => {
    switch (leaderboardType) {
      case 0:
        return 'Partners';
      case 1:
        return 'Earnings';
      case 2:
        return 'Performance Score';
      default:
        return '';
    }
  };

  if (loading) {
    return (
      <div className={classes.loading}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4" gutterBottom>
          <EmojiEventsIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
          Agent Leaderboards
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Top performing agents across all metrics
        </Typography>
      </div>

      <Paper className={classes.tabs}>
        <Tabs
          value={leaderboardType}
          onChange={(e, newValue) => setLeaderboardType(newValue)}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          <Tab icon={<PeopleIcon />} label="Partners" />
          <Tab icon={<AttachMoneyIcon />} label="Earnings" />
          <Tab icon={<StarIcon />} label="Performance" />
        </Tabs>
      </Paper>

      <div className={classes.filterContainer}>
        <FormControl variant="outlined" style={{ minWidth: 200 }}>
          <InputLabel>Country Filter</InputLabel>
          <Select
            value={countryFilter}
            onChange={(e) => setCountryFilter(e.target.value)}
            label="Country Filter"
          >
            <MenuItem value="all">All Countries</MenuItem>
            {countries.map(country => (
              <MenuItem key={country} value={country}>{country}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      {/* Top 3 Podium */}
      {topThree.length >= 3 && (
        <Grid container spacing={3} style={{ marginBottom: 24 }}>
          {/* 2nd Place */}
          <Grid item xs={12} md={4}>
            <Card className={classes.topThreeCard} style={{ background: 'linear-gradient(135deg, #C0C0C0 0%, #808080 100%)' }}>
              <div className={classes.medalContainer}>🥈</div>
              <Avatar
                src={topThree[1].profileImage}
                className={classes.avatar}
                style={{ width: 80, height: 80, margin: '0 auto 16px' }}
              >
                {topThree[1].firstName.charAt(0)}{topThree[1].lastName.charAt(0)}
              </Avatar>
              <Typography variant="h6">{topThree[1].firstName} {topThree[1].lastName}</Typography>
              <Typography variant="body2" style={{ opacity: 0.9 }}>{topThree[1].country}</Typography>
              <Chip
                label={getTierName(topThree[1].tier)}
                style={{ backgroundColor: getTierColor(topThree[1].tier), color: 'white', marginTop: 8 }}
              />
              <Typography variant="h5" style={{ marginTop: 16 }}>
                {getMetricValue(topThree[1])}
              </Typography>
            </Card>
          </Grid>

          {/* 1st Place */}
          <Grid item xs={12} md={4}>
            <Card className={classes.topThreeCard} style={{ background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)' }}>
              <div className={classes.medalContainer}>🥇</div>
              <Avatar
                src={topThree[0].profileImage}
                className={classes.avatar}
                style={{ width: 100, height: 100, margin: '0 auto 16px' }}
              >
                {topThree[0].firstName.charAt(0)}{topThree[0].lastName.charAt(0)}
              </Avatar>
              <Typography variant="h5">{topThree[0].firstName} {topThree[0].lastName}</Typography>
              <Typography variant="body2" style={{ opacity: 0.9 }}>{topThree[0].country}</Typography>
              <Chip
                label={getTierName(topThree[0].tier)}
                style={{ backgroundColor: getTierColor(topThree[0].tier), color: 'white', marginTop: 8 }}
              />
              <Typography variant="h4" style={{ marginTop: 16, fontWeight: 'bold' }}>
                {getMetricValue(topThree[0])}
              </Typography>
            </Card>
          </Grid>

          {/* 3rd Place */}
          <Grid item xs={12} md={4}>
            <Card className={classes.topThreeCard} style={{ background: 'linear-gradient(135deg, #CD7F32 0%, #8B4513 100%)' }}>
              <div className={classes.medalContainer}>🥉</div>
              <Avatar
                src={topThree[2].profileImage}
                className={classes.avatar}
                style={{ width: 80, height: 80, margin: '0 auto 16px' }}
              >
                {topThree[2].firstName.charAt(0)}{topThree[2].lastName.charAt(0)}
              </Avatar>
              <Typography variant="h6">{topThree[2].firstName} {topThree[2].lastName}</Typography>
              <Typography variant="body2" style={{ opacity: 0.9 }}>{topThree[2].country}</Typography>
              <Chip
                label={getTierName(topThree[2].tier)}
                style={{ backgroundColor: getTierColor(topThree[2].tier), color: 'white', marginTop: 8 }}
              />
              <Typography variant="h5" style={{ marginTop: 16 }}>
                {getMetricValue(topThree[2])}
              </Typography>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Full Leaderboard Table */}
      <Card>
        <CardContent>
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Rank</TableCell>
                  <TableCell>Agent</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Tier</TableCell>
                  <TableCell align="right">{getMetricLabel()}</TableCell>
                  <TableCell align="right">Total Earnings</TableCell>
                  <TableCell align="right">Partners</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaderboardData.map((agent) => (
                  <TableRow key={agent.uid} hover>
                    <TableCell className={agent.rank <= 3 ? classes.topThreeRank : classes.rankCell}>
                      {getRankMedal(agent.rank)}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <Avatar className={classes.avatar} style={{ marginRight: 12 }}>
                          {agent.firstName.charAt(0)}{agent.lastName.charAt(0)}
                        </Avatar>
                        <div>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {agent.firstName} {agent.lastName}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {agent.email}
                          </Typography>
                        </div>
                      </Box>
                    </TableCell>
                    <TableCell>{agent.country}</TableCell>
                    <TableCell>
                      <Chip
                        label={getTierName(agent.tier)}
                        size="small"
                        className={classes.tierChip}
                        style={{ backgroundColor: getTierColor(agent.tier), color: 'white' }}
                      />
                    </TableCell>
                    <TableCell align="right" className={classes.metricValue}>
                      {getMetricValue(agent)}
                    </TableCell>
                    <TableCell align="right">
                      ${agent.totalEarnings.toFixed(2)}
                    </TableCell>
                    <TableCell align="right">
                      {agent.partnersCount}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </div>
  );
}
